import React, { useEffect, useMemo, useState, useCallback } from 'react'
import '../../assets/theme/theme.less'
import { LayoutComponent as Layout } from '../../components'
import { Row, Col, Button, message as alert, Spin, Divider, Card, Space, Checkbox, Input, Collapse } from 'antd'
import { useParams, Link } from 'react-router-dom'
import { request } from '../../redux/api/config'

// Lista consolidada com agrupamento por seção
const FEATURE_GROUPS = [
    {
        group: 'Admin',
        items: [
            { key: 'permission_management', label: 'Gerenciar permissões (feature)' },
            { key: 'admin-permissions/:id', label: 'Admin Permissions (API)' },
        ]
    },
    {
        group: 'Clientes',
        items: [
            { key: 'clientes/geral', label: 'Geral' },
            { key: 'clientes/pendentes', label: 'Pendentes' },
            { key: 'cliente/:id', label: 'Detalhe' },
            { key: 'cliente/:id/novo-contrato', label: 'Novo Contrato' },
            { key: 'clientes/relatorio', label: 'Relatório' },
        ]
    },
    {
        group: 'Aprovações',
        items: [
            { key: 'aprovacoes/aportes', label: 'Aportes' },
            { key: 'aprovacoes/resgates', label: 'Resgates' },
            { key: 'aprovacoes/asa', label: 'Contratos ASA' },
            { key: 'aprovacoes/rfy18', label: 'Contratos RFY18' },
            { key: 'aprovacoes/rfy18-subordinada', label: 'Contratos RFY18 Subordinada' },
            { key: 'aprovacoes/spirit', label: 'Spirit' },
            { key: 'aprovacoes/spirit2', label: 'Spirit2' },
            { key: 'aprovacoes/spirit3', label: 'Spirit3' },
            { key: 'aprovacoes/bats', label: 'BATS' },
        ]
    },
    {
        group: 'Contratos',
        items: [
            { key: 'contratos/vencidos', label: 'Vencidos' },
            { key: 'contrato/:id', label: 'Detalhe do Contrato' },
        ]
    },
    {
        group: 'Comissões',
        items: [
            { key: 'comissoes', label: 'Comissões' },
        ]
    },
    {
        group: 'Saques',
        items: [
            { key: 'saques', label: 'Pendentes' },
            { key: 'saques/historico', label: 'Histórico' },
        ]
    },
    {
        group: 'Operacional',
        items: [
            { key: 'operacional/asa', label: 'Rise Fixed Yield - Classe Sênior' },
            { key: 'operacional/rfy18', label: 'RFY18 - Classe Sênior (operacional)' },
            { key: 'operacional/rfy18-subordinada', label: 'RFY18 - Classe Subordinada (operacional)' },
            { key: 'operacional/spirit', label: 'Liquidity Yield' },
            { key: 'operacional/spirit2', label: 'Rise Fixed Yield - Classe Subordinada' },
            { key: 'operacional/spirit3', label: 'Rise Liquidity Yield 3' },
            { key: 'operacional/bats', label: 'BATS' },
            { key: 'operacional/:id', label: 'Operacional Dinâmico (:id)' },
        ]
    },
    // Monitor removido a pedido
]

const UsersPermissions = () => {
    const { id } = useParams()
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [selected, setSelected] = useState([])
    const [query, setQuery] = useState('')

    const allKeys = useMemo(() => FEATURE_GROUPS.flatMap(g => g.items.map(i => i.key)), [])
    const mapFeaturesToKeys = useCallback((features = []) => {
        const set = new Set(allKeys)
        return (features || []).filter((f) => set.has(f))
    }, [allKeys])

    const mapUrlsToKeys = (urls = []) => {
        const flatKeys = FEATURE_GROUPS.flatMap(g => g.items.map(i => i.key))
        const normalized = (urls || []).map((u) => {
            let s = String(u || '')
            s = s.replace(/^\//, '')
            s = s.replace(/\*/g, ':id')
            return s
        })
        const set = new Set()
        normalized.forEach((n) => {
            if (flatKeys.includes(n)) set.add(n)
        })
        return Array.from(set)
    }

    // Carregar permissões atuais do usuário
    useEffect(() => {
        let mounted = true
        const load = async () => {
            try {
                setLoading(true)
                const res = await request('admin-permissions/user-features', 'POST', { user_id: id })
                const urls = res?.data?.urls || res?.urls || []
                const features = res?.data?.features || res?.features || []
                const preselected = Array.from(new Set([
                    ...mapUrlsToKeys(urls),
                    ...mapFeaturesToKeys(features)
                ]))
                if (mounted) setSelected(preselected)
            } catch (e) {
                alert.error('Não foi possível carregar as permissões')
            } finally {
                if (mounted) setLoading(false)
            }
        }
        load()
        return () => { mounted = false }
    }, [id, mapFeaturesToKeys])

    const filteredGroups = useMemo(() => {
        const q = (query || '').trim().toLowerCase()
        if (!q) return FEATURE_GROUPS
        return FEATURE_GROUPS
            .map(g => ({
                ...g,
                items: g.items.filter(i => `${g.group} ${i.label} ${i.key}`.toLowerCase().includes(q))
            }))
            .filter(g => g.items.length > 0)
    }, [query])

    const selectAllInGroup = (groupLabel) => {
        const group = FEATURE_GROUPS.find(g => g.group === groupLabel)
        if (!group) return
        const keys = group.items.map(i => i.key)
        setSelected(prev => Array.from(new Set([...(prev || []), ...keys])))
    }

    const clearGroup = (groupLabel) => {
        const group = FEATURE_GROUPS.find(g => g.group === groupLabel)
        if (!group) return
        const keys = new Set(group.items.map(i => i.key))
        setSelected(prev => (prev || []).filter(k => !keys.has(k)))
    }

    // Atualiza seleção global preservando seleções de outros grupos
    const handleGroupChange = (group, groupValues) => {
        const groupKeys = new Set(group.items.map(i => i.key))
        setSelected(prev => {
            const rest = (prev || []).filter(k => !groupKeys.has(k))
            return Array.from(new Set([...(rest || []), ...(groupValues || [])]))
        })
    }

    const buildPermissionPayload = (vals) => {
        const nowActive = (vals || [])
        const map = new Map()
        // Monta mapa label por key para descrição amigável
        FEATURE_GROUPS.forEach(g => g.items.forEach(i => {
            map.set(i.key, { group: g.group, label: i.label })
        }))

        const normalize = (v) => {
            if (v === 'asa') return 'operacional/asa'
            if (v === 'rfy18') return 'operacional/rfy18'
            if (v === 'rfy18-subordinada') return 'operacional/rfy18-subordinada'
            if (v === 'spirit') return 'operacional/spirit'
            if (v === 'spirit2') return 'operacional/spirit2'
            if (v === 'spirit3') return 'operacional/spirit3'
            if (v === 'bats') return 'operacional/bats'
            if (v === 'operacional_dynamic') return 'operacional/:id'
            return v
        }

        return nowActive.map((v) => {
            const key = normalize(v)
            const meta = map.get(key) || {}
            // permission_name: substituir '/' por '_' e ':' por ''
            const permName = key.replace(/\//g, '_').replace(/:/g, '')
            const isAdmin = key === 'permission_management' || key.startsWith('admin-permissions')
            return {
                permission_name: isAdmin ? `${permName}_u${id}` : permName,
                permission_description: meta.label || key,
                url_pattern: `/${key.replace(/:\\w+/g, '*')}`,
                http_method: 'ALL',
                feature_name: isAdmin ? 'permission_management' : `ui_${(meta.group || 'app').toLowerCase()}`.replace(/\s+/g, ''),
                feature_category: isAdmin ? 'admin' : (meta.group || 'app').toLowerCase(),
                is_active: true,
                metadata: { origin: 'ui_admin' }
            }
        })
    }

    const onSave = async () => {
        try {
            setSaving(true)
            const permissions = buildPermissionPayload(selected)
            const payload = { user_id: id, permissions }
            const res = await request('admin-permissions/permissions/bulk', 'POST', payload)
            if (res?.success === false) throw new Error()
            alert.success('Permissões atualizadas')
        } catch (e) {
            alert.error('Falha ao salvar permissões')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Layout width>
            <Row>
                <Col md={24}>
                    <h1 className="title page-title">Permissões do Usuário</h1>
                </Col>
                <Col md={24} className="mt-10">
                    <Space size={12} wrap>
                        <Link to={`/cliente/${id}`}>
                            <Button>Voltar</Button>
                        </Link>
                        <Button type="primary" loading={saving} onClick={onSave}>Salvar alterações</Button>
                    </Space>
                </Col>
                <Col md={24} className="mt-20">
                    {loading ? (
                        <Spin />
                    ) : (
                        <Card bodyStyle={{ padding: 16 }}>
                            <label className="tx-placeholder font-bold">Permissões</label>
                            <div className="mt-10">
                                <Input.Search
                                    placeholder="Buscar por seção ou rota"
                                    allowClear
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </div>
                            <Divider />
                            <Collapse bordered={false} defaultActiveKey={filteredGroups.map(g => g.group)}>
                                {filteredGroups.map((g) => {
                                    const groupKeys = new Set(g.items.map(i => i.key))
                                    const checkedCount = (selected || []).filter(k => groupKeys.has(k)).length
                                    return (
                                        <Collapse.Panel header={`${g.group} (${checkedCount}/${g.items.length})`} key={g.group}>
                                            <div className="mt-5">
                                                <Space>
                                                    <Button size="small" onClick={() => selectAllInGroup(g.group)}>Selecionar todos</Button>
                                                    <Button size="small" onClick={() => clearGroup(g.group)}>Limpar</Button>
                                                </Space>
                                            </div>
                                            <div className="mt-10">
                                                <Checkbox.Group value={(selected || []).filter(k => groupKeys.has(k))} onChange={(vals) => handleGroupChange(g, vals)}>
                                                    <Row gutter={[12,12]} align="top">
                                                        {g.items.map((i) => (
                                                            <Col xs={24} sm={24} md={24} lg={24} key={i.key}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <Checkbox value={i.key} />
                                                                    <span style={{ whiteSpace: 'normal', wordBreak: 'normal' }}>{i.label}</span>
                                                                </div>
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                </Checkbox.Group>
                                            </div>
                                        </Collapse.Panel>
                                    )
                                })}
                            </Collapse>
                        </Card>
                    )}
                </Col>
            </Row>
        </Layout>
    )
}

export default UsersPermissions


